import React, { useState, useEffect } from 'react';
import './UploadCoupons.scss';
import {
  Breadcrumbs,
  Typography,
  Link,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@material-ui/core';
import { CloudUpload, Close, Edit } from '@material-ui/icons';
import { http, useNavigationService, AlertService } from '../../fx-core';

const IMAGE_BASE_URL = 'https://localitesstrg.blob.core.windows.net/commkit-ctn/';
const PLACEHOLDER_IMAGE =
  "data:image/svg+xml;charset=UTF-8,%3csvg width='800' height='400' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='800' height='400' fill='%23f0f0f0'/%3e%3ctext x='50%25' y='50%25' font-family='Arial,sans-serif' font-size='24' fill='%23999' text-anchor='middle' dy='.3em'%3eNo Image%3c/text%3e%3c/svg%3e";

const UploadCoupons = (props: any) => {
  const { routeData, back } = useNavigationService();

  // ============ STATE ============
  const [openCouponDialog, setOpenCouponDialog] = useState<boolean>(false);
  const [couponFormData, setCouponFormData] = useState({
    couponName: '',
    link: '',
    description: '',
    file: null as File | null,
    fileName: '',
    existingImagePath: ''
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [couponsList, setCouponsList] = useState<any[]>([]);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState<boolean>(false);
  const [editingCouponId, setEditingCouponId] = useState<number | null>(null);

  // ============ HELPERS ============
  const getImageUrl = (imagePath: string | null | undefined): string => {
    if (!imagePath) return PLACEHOLDER_IMAGE;
    if (imagePath.startsWith('http')) return imagePath;
    return `${IMAGE_BASE_URL}${imagePath}`;
  };

  const navToBack = () => {
    back();
  };

  // ============ FETCH ALL COUPONS ============
  const fetchAllCoupons = async () => {
    setIsLoadingCoupons(true);
    try {
      const apiOptions: any = {
        url: 'banner/list/all',
        data: {}
      };
      const response = await http.get(apiOptions);
      if (response && response.data && response.data.banners) {
        setCouponsList(response.data.banners);
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
      AlertService.showErrorMsg('Failed to load coupons');
    } finally {
      setIsLoadingCoupons(false);
    }
  };

  useEffect(() => {
    fetchAllCoupons();
  }, []);

  // ============ COUPON DIALOG HANDLERS ============
  const handleEditCoupon = (coupon: any) => {
    setEditingCouponId(coupon.id);
    setCouponFormData({
      couponName: coupon.bannerName || '',
      link: coupon.link || '',
      description: coupon.description || '',
      file: null,
      fileName: '',
      existingImagePath: coupon.imagePath || ''
    });
    setOpenCouponDialog(true);
  };

  const handleCloseCouponDialog = () => {
    setOpenCouponDialog(false);
    handleCouponClear();
    setEditingCouponId(null);
  };

  const handleCouponInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCouponFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCouponFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCouponFormData(prev => ({
        ...prev,
        file: file,
        fileName: file.name
      }));
    }
  };

  // ============ SUBMIT: UPDATE ONLY ============
  const handleCouponSubmit = async () => {
    if (!couponFormData.couponName) {
      AlertService.showErrorMsg('Coupon name is required');
      return;
    }

    try {
      setIsSubmitting(true);

      const uploadData = new FormData();

      if (couponFormData.file) {
        uploadData.append('file', couponFormData.file);
      }

      uploadData.append('bannerName', couponFormData.couponName);
      uploadData.append('link', couponFormData.link || '');
      uploadData.append('description', couponFormData.description || '');
      uploadData.append(
        'communityId',
        routeData?.communityId ? String(routeData.communityId) : '20052'
      );

      if (editingCouponId) {
        uploadData.append('id', editingCouponId.toString());

        const response = await http.put({
          url: 'banner/saveimage',
          data: uploadData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response && response.data) {
          AlertService.showSuccessMsg('Coupon updated successfully');
        }
      }

      handleCouponClear();
      const fileInput = document.getElementById(
        'coupon-file-input'
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      setOpenCouponDialog(false);
      setEditingCouponId(null);
      fetchAllCoupons();
    } catch (error) {
      console.error('Error updating coupon:', error);
      AlertService.showErrorMsg('Failed to save coupon. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCouponClear = () => {
    setCouponFormData({
      couponName: '',
      link: '',
      description: '',
      file: null,
      fileName: '',
      existingImagePath: ''
    });
    const fileInput = document.getElementById(
      'coupon-file-input'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const isUpdateCouponDisabled = (): boolean => {
    if (!couponFormData.couponName) return true;
    return false;
  };

  // Filter: show only coupons where couponId !== 0
  const filteredCouponsList = couponsList.filter((coupon: any) => coupon.couponId !== 0);

  // ============ RENDER ============
  return (
    <div className="uploadcoupons-ui pt-5">
      {/* ============ BREADCRUMB SECTION - SAME AS CommunityMonthlyIncome ============ */}
      <div className="title-font custom-head pl-4 pt-5">
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="primary"
            className="custom-link pointer breadcrumbs-style"
            onClick={navToBack}
          >
            <b>{routeData?.communityName || 'Community'}</b>
          </Link>
          <Typography color="primary" className="breadcrumbs-style">
            Promotions
          </Typography>
        </Breadcrumbs>
      </div>

      <div className="content-container">
        <div className="content">
          <div className="promotion-banner-section">
            {isLoadingCoupons ? (
              <div className="text-center p-5">
                <Typography variant="h6" style={{ color: '#666' }}>
                  Loading coupons...
                </Typography>
              </div>
            ) : filteredCouponsList.length === 0 ? (
              <div className="empty-state p-5">
                <h4 className="text-center">No coupons available</h4>
                <p className="text-center text-muted">
                  No coupons to edit
                </p>
              </div>
            ) : (
              <div className="banners-list p-4">
                <TableContainer component={Paper}>
                  <Table size="small" className="table-striped">
                    <TableHead>
                      <TableRow>
                        <TableCell>Image</TableCell>
                        <TableCell>Coupon Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Link</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCouponsList.map((coupon: any) => (
                        <TableRow key={coupon.id}>
                          <TableCell>
                            <img
                              src={getImageUrl(coupon.imagePath)}
                              alt={coupon.bannerName}
                              style={{
                                width: '100px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '4px'
                              }}
                              onError={(e: any) => {
                                e.target.src = PLACEHOLDER_IMAGE;
                              }}
                            />
                          </TableCell>
                          <TableCell>{coupon.bannerName}</TableCell>
                          <TableCell>{coupon.description || '-'}</TableCell>
                          <TableCell>
                            {coupon.link ? (
                              <a
                                href={coupon.link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {coupon.link}
                              </a>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleEditCoupon(coupon)}
                              className="edit-btn"
                              title="Edit"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============ EDIT COUPON DIALOG ============ */}
      <Dialog
        open={openCouponDialog}
        onClose={handleCloseCouponDialog}
        maxWidth="sm"
        fullWidth
        className="promotion-banner-dialog"
      >
        <DialogTitle className="dialog-header">
          <div className="d-flex justify-content-between align-items-center">
            <span>Edit Coupon</span>
            <IconButton
              size="small"
              onClick={handleCloseCouponDialog}
              className="close-btn"
            >
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className="dialog-content">
          {editingCouponId && couponFormData.existingImagePath && (
            <div className="mb-3 current-image-container">
              <Typography variant="subtitle2" gutterBottom>
                Current Image:
              </Typography>
              <img
                src={getImageUrl(couponFormData.existingImagePath)}
                alt="Current coupon"
                className="current-banner-image"
                onError={(e: any) => {
                  e.target.src = PLACEHOLDER_IMAGE;
                }}
              />
              <Typography
                variant="caption"
                display="block"
                className="image-help-text"
              >
                Upload a new image below to replace this one (optional)
              </Typography>
            </div>
          )}

          <div className="mb-3">
            <TextField
              name="couponName"
              label="Coupon Name *"
              variant="outlined"
              fullWidth
              value={couponFormData.couponName}
              onChange={handleCouponInputChange}
              placeholder="e.g., 20% Off Sale"
              margin="normal"
            />
          </div>

          <div className="mb-3">
            <TextField
              name="link"
              label="Coupon Link (URL)"
              variant="outlined"
              fullWidth
              value={couponFormData.link}
              onChange={handleCouponInputChange}
              placeholder="https://localites.in/coupon"
              margin="normal"
            />
          </div>

          <div className="mb-3">
            <TextField
              name="description"
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              value={couponFormData.description}
              onChange={handleCouponInputChange}
              placeholder="Valid till end of month"
              margin="normal"
            />
          </div>

          {/* ========== FILE UPLOAD ========== */}
          <div className="mb-3">
            <div className="file-upload-section">
              <input
                id="coupon-file-input"
                type="file"
                accept="image/*"
                onChange={handleCouponFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="coupon-file-input" className="w-100">
                <Button
                  variant="outlined"
                  color="primary"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                >
                  {couponFormData.fileName ||
                    'Change Coupon Image (Optional)'}
                </Button>
              </label>
              {couponFormData.fileName && (
                <div className="mt-2 text-muted file-name">
                  ✓ New file selected:{' '}
                  <strong>{couponFormData.fileName}</strong>
                </div>
              )}
            </div>
          </div>
        </DialogContent>

        <DialogActions className="dialog-actions p-3">
          <Button onClick={handleCloseCouponDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleCouponSubmit}
            variant="contained"
            disabled={isUpdateCouponDisabled() || isSubmitting}
            className="create-banner-button"
          >
            {isSubmitting ? 'Saving...' : 'Update Coupon'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UploadCoupons;